import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { marketingApi } from '../../api/marketing';
import type { MarketUser } from '../../api/marketing';

const ContactsPage: React.FC = () => {
  const [contacts, setContacts] = useState<MarketUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadOptions, setUploadOptions] = useState({
    validate_emails: true,
    update_existing: true,
  });
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await marketingApi.getUsers({
        page: page + 1,
        page_size: rowsPerPage,
        search: searchQuery,
        source: sourceFilter || undefined,
      });
      setContacts(response.results);
      setTotalCount(response.count);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [page, rowsPerPage, searchQuery, sourceFilter]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    try {
      setUploading(true);
      const result = await marketingApi.uploadCSV(
        uploadFile,
        uploadOptions.validate_emails,
        uploadOptions.update_existing
      );
      setUploadResult(result);

      // Refresh contacts list
      await fetchContacts();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadResult({
        success: false,
        message: 'Upload failed',
        errors: ['Failed to upload file'],
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setUploadFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const csv = 'email,name,source,created_at\njohn@example.com,John Doe,website,2025-01-15\njane@example.com,Jane Smith,newsletter,2025-02-20';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getSourceColor = (source: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    const colors: Record<string, any> = {
      csv: 'default',
      sqlflow: 'primary',
      manual: 'secondary',
      api: 'success',
      signup: 'warning',
    };
    return colors[source] || 'default';
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Contacts
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadTemplate}
            sx={{ mr: 2 }}
          >
            Download Template
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 400 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              label="Source"
            >
              <MenuItem value="">All Sources</MenuItem>
              <MenuItem value="csv">CSV Import</MenuItem>
              <MenuItem value="sqlflow">SQLFlow</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="api">API</MenuItem>
              <MenuItem value="signup">Signup</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchContacts} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Contacts Table */}
      <Paper>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Valid</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell>
                    <Typography variant="body2">{contact.email}</Typography>
                  </TableCell>
                  <TableCell>{contact.name || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={contact.source}
                      size="small"
                      color={getSourceColor(contact.source)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={contact.is_valid ? 'Valid email' : 'Invalid email'}>
                      {contact.is_valid ? (
                        <ValidIcon color="success" fontSize="small" />
                      ) : (
                        <InvalidIcon color="error" fontSize="small" />
                      )}
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {contact.last_activity
                      ? new Date(contact.last_activity).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(contact.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {contacts.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      No contacts found. Upload a CSV to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={!uploading ? handleCloseUploadDialog : undefined}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Contacts CSV</DialogTitle>
        <DialogContent>
          {!uploadResult ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Expected columns: email, name, source
              </Alert>
              {uploadFile && (
                <Box mb={2}>
                  <Typography variant="body2" gutterBottom>
                    Selected file: <strong>{uploadFile.name}</strong>
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Size: {(uploadFile.size / 1024).toFixed(2)} KB
                  </Typography>
                </Box>
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadOptions.validate_emails}
                    onChange={(e) =>
                      setUploadOptions({ ...uploadOptions, validate_emails: e.target.checked })
                    }
                    disabled={uploading}
                  />
                }
                label="Validate email addresses"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={uploadOptions.update_existing}
                    onChange={(e) =>
                      setUploadOptions({ ...uploadOptions, update_existing: e.target.checked })
                    }
                    disabled={uploading}
                  />
                }
                label="Update existing contacts"
              />
              {uploading && (
                <Box mt={2}>
                  <LinearProgress />
                  <Typography variant="body2" color="textSecondary" align="center" mt={1}>
                    Uploading and processing...
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box>
              <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {uploadResult.message}
              </Alert>
              {uploadResult.success && (
                <Box>
                  <Typography variant="body2" gutterBottom>
                    <strong>Results:</strong>
                  </Typography>
                  <Typography variant="body2">• Total processed: {uploadResult.total}</Typography>
                  <Typography variant="body2">• Created: {uploadResult.created}</Typography>
                  <Typography variant="body2">• Updated: {uploadResult.updated}</Typography>
                  <Typography variant="body2">• Skipped: {uploadResult.skipped}</Typography>
                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <>
                      <Typography variant="body2" color="error" mt={1}>
                        <strong>Errors:</strong>
                      </Typography>
                      {uploadResult.errors.slice(0, 5).map((error: string, index: number) => (
                        <Typography key={index} variant="caption" color="error">
                          • {error}
                        </Typography>
                      ))}
                      {uploadResult.errors.length > 5 && (
                        <Typography variant="caption" color="error">
                          ... and {uploadResult.errors.length - 5} more
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!uploadResult ? (
            <>
              <Button onClick={handleCloseUploadDialog} disabled={uploading}>
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                variant="contained"
                disabled={!uploadFile || uploading}
                startIcon={uploading ? null : <CloudUploadIcon />}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseUploadDialog} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactsPage;